import { Button, Flex, Loading, ScalingButtonGroup, Space } from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import {
  useIndicatorPermission,
  useProjectPermission,
} from '@/features/authentication/_hooks/permission';
import { ActivityTree } from '@/features/mold/components';
import { parseTreeToNodesAndEdges } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import {
  useActivityTree,
  useUpdateActivityTree,
} from '@/features/project/projects/detail/activities/_hooks/activity';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'reactflow';
import { ActivityFlowEdgesDrawer } from '../ActivityFlowEdgesDrawer';
import { ActivityFlowTable } from './ActivityFlowTable';

type Props = {
  setTreeModifyCount: Dispatch<SetStateAction<number>>;
};

export const ActivityFlow: FC<Props> = ({ setTreeModifyCount }) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const editProjectPermission = useProjectPermission(true);
  const editIndicatorPermission = useIndicatorPermission(true);
  const hasEditPermission =
    editProjectPermission.data && editIndicatorPermission.data;

  const tree = useActivityTree(projectId, parseTreeToNodesAndEdges);

  const { mutateAsync: updateActivityTreeAsync } = useUpdateActivityTree();

  if (!tree.data || tree.isPending || project.isPending || !project.data) {
    return <Loading />;
  }

  return (
    <>
      <Flex vertical gap={16}>
        <ActivityTree
          initialNodes={tree.data.nodes}
          initialEdges={tree.data.edges}
          setTreeModifyCount={setTreeModifyCount}
          panelNode={({ nodes, edges }) => (
            <Panel position="top-right">
              <Space>
                <ScalingButtonGroup />
                {hasEditPermission && !project.data?.isLocked && (
                  <PublishedProjectMutableGuard>
                    <Button
                      htmlType="button"
                      onClick={async () => {
                        await updateActivityTreeAsync({
                          projectId,
                          nodes: nodes.map((node) => ({
                            id: node.id,
                            position: node.position,
                          })),
                          edges: edges.map((edge) => ({
                            id: edge.id,
                            sourceNodeId: edge.source,
                            targetNodeId: edge.target,
                            label: edge.data.edgeLabel,
                          })),
                        });
                        await openMessage(
                          'success',
                          t('message-update-completed'),
                        );
                      }}
                    >
                      {t('update-position')}
                    </Button>
                  </PublishedProjectMutableGuard>
                )}
              </Space>
            </Panel>
          )}
        />

        <ActivityFlowTable />
      </Flex>

      <ActivityFlowEdgesDrawer />
    </>
  );
};
